import React, { useState } from 'react';
import { generatePath } from 'react-router-dom';
import { Box, ButtonBase, Collapse, Typography } from '@mui/material';
import { FileDownloadOutlined, MailOutline } from '@mui/icons-material';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import type { UsageTimelineItem } from 'c-sdk';
import { PostAuthRoutes } from 'c-routes';
import { useCommonTranslation } from 'c-translation';
import { useDateUtils } from 'c-hooks';
import MuiRouterLink from 'c-components/MuiRouterLink';
import { useFormatDuration } from './usageLib';
import type { ParamChip } from './activityLabels';
import { pageLabel, useActivityLabel, useDeviceLabel, useParamChips } from './activityLabels';

/**
 * A user's activity as the design canvas draws it (board 3, "User activity"): Day › Session ›
 * Page visit, with the emails we sent them as lines of their own between sessions. Each visit is one line (time, page, `#id · campaign`, time on page), with the filters
 * chosen beneath in plain text and actions as tags. Queries, timings, IP and device sit behind
 * "Show details". Calls not made from a page show as a hollow, muted line.
 */

export type TimelineGroup = {
    key: string;
    /** The page visit's template, or null for calls not made from a page. */
    page: string | null;
    /** The page view row itself, once it has arrived (it is sent when the user leaves the page). */
    visit: UsageTimelineItem | null;
    /** What happened during the visit: API queries and tracked actions, newest first. */
    items: UsageTimelineItem[];
    /** An email we sent them: always a line of its own, never part of a visit. */
    email?: UsageTimelineItem;
};

/**
 * Groups timeline rows by the page visit that produced them (`page_view_id`), keeping the
 * timeline's newest-first order of first appearance. A row with no visit stands alone.
 */
export const groupTimeline = (rows: UsageTimelineItem[]): TimelineGroup[] => {
    const groups: TimelineGroup[] = [];
    const byVisit = new Map<string, TimelineGroup>();

    rows.forEach(row => {
        if (row.kind === 'email') {
            groups.push({ key: `email-${row.id}`, page: null, visit: null, items: [], email: row });
            return;
        }
        if (!row.page_view_id) {
            groups.push({
                key: `row-${row.id}`,
                page: row.kind === 'page_view' ? row.name : null,
                visit: row.kind === 'page_view' ? row : null,
                items: row.kind === 'page_view' ? [] : [row],
            });
            return;
        }

        let group = byVisit.get(row.page_view_id);
        if (!group) {
            group = { key: row.page_view_id, page: row.page, visit: null, items: [] };
            byVisit.set(row.page_view_id, group);
            groups.push(group);
        }
        if (row.kind === 'page_view') {
            group.visit = row;
            group.page = row.name;
        } else {
            group.items.push(row);
            group.page = group.page ?? row.page;
        }
    });

    return groups;
};

/** Consecutive groups with no page (background calls) become one, so they read as one line. */
export const mergeBackground = (groups: TimelineGroup[]): TimelineGroup[] => {
    const merged: TimelineGroup[] = [];
    groups.forEach(group => {
        const previous = merged[merged.length - 1];
        if (!group.page && !group.email && previous && !previous.page && !previous.email) {
            previous.items.push(...group.items);
        } else {
            merged.push({ ...group, items: [...group.items] });
        }
    });
    return merged;
};

const groupRow = (group: TimelineGroup) => group.visit ?? group.email ?? group.items[0];

/** When a group happened: its page view, else its newest row. */
const groupTime = (group: TimelineGroup): string => groupRow(group)?.occurred_at ?? '';

/** The campaign a visit was about: the page's own subject, never one from a background query. */
export const visitCampaign = (group: TimelineGroup): UsageTimelineItem | undefined => {
    if (group.visit?.subject_type === 'campaign') return group.visit;
    if (group.page?.startsWith('/campaigns/:id')) {
        return group.items.find(item => item.subject_type === 'campaign');
    }
    return undefined;
};

/**
 * Every distinct filter chosen across a visit, the page's own URL filters first. The page's bare
 * "Search x" is dropped when a query says what that same text searched ("Search campaigns x").
 */
export const visitFilters = (
    group: TimelineGroup,
    chips: (params: UsageTimelineItem['params'], requestName?: string) => ParamChip[],
    searchLabel: string,
): ParamChip[] => {
    const seen = new Map<string, ParamChip>();
    [group.visit, ...group.items.filter(item => item.kind === 'request')].forEach(item => {
        if (!item) return;
        chips(item.params, item.kind === 'request' ? item.name : undefined).forEach(chip => {
            const key = `${chip.label}: ${chip.value}`;
            if (!seen.has(key)) seen.set(key, chip);
        });
    });
    const named = new Set(
        [...seen.values()]
            .filter(chip => chip.label !== searchLabel && chip.label.startsWith(searchLabel))
            .map(chip => chip.value),
    );
    return [...seen.values()].filter(
        chip => !(chip.label === searchLabel && named.has(chip.value)),
    );
};

/** A browser tab's visits, or (`emails`) emails sent in between, which belong to no tab. */
export type TimelineSession = { key: string; groups: TimelineGroup[]; emails?: boolean };
export type TimelineDay = { key: string; date: string; sessions: TimelineSession[] };

/**
 * Days, then sessions within each day, newest first. A session is a browser tab
 * (`session_id`); a group with none (background requests) joins the session it sits in. Emails
 * sent between sessions sit on their own, outside any tab.
 */
export const dayAndSession = (groups: TimelineGroup[]): TimelineDay[] => {
    const days: TimelineDay[] = [];
    groups.forEach(group => {
        const time = groupTime(group);
        const dayKey = time ? format(parseISO(time), 'yyyy-MM-dd') : 'unknown';
        let day = days[days.length - 1];
        if (!day || day.key !== dayKey) {
            day = { key: dayKey, date: time, sessions: [] };
            days.push(day);
        }
        let session = day.sessions[day.sessions.length - 1];
        if (group.email) {
            if (!session?.emails) {
                session = { key: group.key, groups: [], emails: true };
                day.sessions.push(session);
            }
            session.groups.push(group);
            return;
        }
        const sessionId = groupRow(group)?.session_id ?? null;
        if (!session || session.emails || (sessionId !== null && session.key !== sessionId)) {
            session = { key: sessionId ?? `none-${group.key}`, groups: [] };
            day.sessions.push(session);
        }
        session.groups.push(group);
    });
    return days;
};

/** "#14791 · PEOPLES POSTCODE LOTTERY", linked to the campaign. */
export const CampaignRef = ({ id, name }: { id: string | number; name: string | null }) => {
    const t = useCommonTranslation();
    return (
        <MuiRouterLink
            to={generatePath(PostAuthRoutes.Campaigns.CampaignOverview, { id })}
            variant="body2"
            underline="hover"
            fontWeight={500}
        >
            {name ? t('Modules.Admin.Usage.campaignRef', { id, name }) : `#${id}`}
        </MuiRouterLink>
    );
};

const useClock = () => {
    const { formatDateString } = useDateUtils();
    return (date: string) =>
        date
            ? formatDateString({
                  date,
                  dateFormatString: '',
                  removeTime: false,
                  timeFormat: 'HH:mm',
              }).trim()
            : '';
};

/** Filters as one quiet line: "Dates 1–30 Oct 2026   Scheduled In, Out   Group by Hour". */
const FilterLine = ({ chips }: { chips: ParamChip[] }) =>
    chips.length === 0 ? null : (
        <Typography
            variant="caption"
            color="text.secondary"
            component="p"
            sx={{ m: 0, lineHeight: 1.6 }}
        >
            {chips.map(chip => (
                <Box
                    component="span"
                    key={`${chip.label}-${chip.value}`}
                    sx={{ whiteSpace: 'nowrap', mr: 1.5 }}
                >
                    <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>
                        {chip.label}
                    </Box>{' '}
                    {chip.value}
                </Box>
            ))}
        </Typography>
    );

const COLUMNS = '52px 22px minmax(0, 1fr)';

/** The rail: a dot on a vertical line, filled for a page visit, hollow for background calls. */
const Rail = ({ strong }: { strong: boolean }) => (
    <Box
        aria-hidden="true"
        sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}
    >
        <Box sx={{ position: 'absolute', top: 0, bottom: -14, width: '2px', bgcolor: 'divider' }} />
        <Box
            sx={{
                position: 'relative',
                mt: '5px',
                width: 10,
                height: 10,
                boxSizing: 'border-box',
                borderRadius: '50%',
                bgcolor: strong ? 'primary.main' : 'background.paper',
                border: 2,
                borderColor: strong ? 'primary.main' : 'text.disabled',
            }}
        />
    </Box>
);

const Details = ({ group }: { group: TimelineGroup }) => {
    const t = useCommonTranslation();
    const label = useActivityLabel();
    const chips = useParamChips();
    const deviceLabel = useDeviceLabel();
    const queries = group.items.filter(item => item.kind === 'request');
    const anchor = groupRow(group);

    return (
        <Box
            sx={{
                mt: 0.5,
                px: 1.5,
                py: 1.25,
                borderRadius: 2,
                bgcolor: 'action.hover',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}
        >
            {anchor?.ip && (
                <Typography variant="caption" color="text.secondary">
                    {t('Modules.Admin.Usage.connection', {
                        ip: anchor.ip,
                        device: deviceLabel(anchor),
                    })}
                </Typography>
            )}
            {queries.map(item => {
                const detail = chips(item.params)
                    .map(chip => `${chip.label} ${chip.value}`)
                    .join(' · ');
                // No response time: readers took it for time spent.
                return (
                    <Typography
                        key={item.id}
                        variant="body2"
                        sx={{ minWidth: 0 }}
                        title={item.name}
                    >
                        <Box component="span" sx={{ fontWeight: 500 }}>
                            {label(item)}
                        </Box>
                        {detail && (
                            <Box component="span" sx={{ color: 'text.secondary' }}>
                                {' '}
                                {detail}
                            </Box>
                        )}
                    </Typography>
                );
            })}
        </Box>
    );
};

const Visit = ({ group }: { group: TimelineGroup }) => {
    const t = useCommonTranslation();
    const label = useActivityLabel();
    const chips = useParamChips();
    const clock = useClock();
    const formatDuration = useFormatDuration();
    const [open, setOpen] = useState(false);

    const actions = group.items.filter(item => item.kind === 'event');
    const queries = group.items.filter(item => item.kind === 'request');
    const campaign = visitCampaign(group);
    const isBackground = !group.page;
    const title = isBackground
        ? t('Modules.Admin.Usage.backgroundRequests', { count: group.items.length })
        : pageLabel(group.page as string);
    const filters = isBackground
        ? []
        : visitFilters(group, chips, t('Modules.Admin.Usage.params.search'));

    return (
        <Box
            component="li"
            aria-label={title}
            sx={{ display: 'grid', gridTemplateColumns: COLUMNS, columnGap: 1.5, pb: 1.75 }}
        >
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textAlign: 'right', pt: '2px', fontVariantNumeric: 'tabular-nums' }}
            >
                {clock(groupTime(group))}
            </Typography>
            <Rail strong={!isBackground} />
            <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                    <Typography
                        variant="body2"
                        component="h4"
                        sx={{
                            m: 0,
                            fontWeight: isBackground ? 400 : 600,
                            color: isBackground ? 'text.secondary' : 'text.primary',
                        }}
                    >
                        {title}
                    </Typography>
                    {campaign?.subject_id && (
                        <CampaignRef id={campaign.subject_id} name={campaign.subject_name} />
                    )}
                    {group.visit?.dwell_ms != null && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                ml: 'auto',
                                whiteSpace: 'nowrap',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {formatDuration(group.visit.dwell_ms)}
                        </Typography>
                    )}
                </Box>
                <FilterLine chips={filters} />
                {actions.map(action => (
                    <Box
                        key={action.id}
                        sx={{
                            alignSelf: 'flex-start',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1,
                            py: 0.375,
                            borderRadius: 1.5,
                            bgcolor: 'action.selected',
                            color: 'primary.dark',
                        }}
                    >
                        <FileDownloadOutlined sx={{ fontSize: 15 }} aria-hidden="true" />
                        <Typography variant="body2" fontWeight={500}>
                            {label(action)}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                            {clock(action.occurred_at)}
                        </Typography>
                    </Box>
                ))}
                {queries.length > 0 && (
                    <ButtonBase
                        onClick={() => setOpen(!open)}
                        aria-expanded={open}
                        title={
                            isBackground
                                ? t('Modules.Admin.Usage.backgroundRequestsHint')
                                : undefined
                        }
                        sx={{
                            alignSelf: 'flex-start',
                            typography: 'caption',
                            color: 'text.secondary',
                            borderRadius: 1,
                            px: 0.25,
                            '&:hover': { color: 'text.primary' },
                        }}
                    >
                        {open
                            ? t('Modules.Admin.Usage.hideDetails')
                            : t('Modules.Admin.Usage.showDetails', { count: queries.length })}
                    </ButtonBase>
                )}
                <Collapse in={open} unmountOnExit>
                    <Details group={group} />
                </Collapse>
            </Box>
        </Box>
    );
};

/** "Email · Campaign signed off  #14791 · …", its subject beneath, the body behind a toggle. */
const EmailLine = ({ row }: { row: UsageTimelineItem }) => {
    const t = useCommonTranslation();
    const label = useActivityLabel();
    const clock = useClock();
    const [open, setOpen] = useState(false);
    const title = t('Modules.Admin.Usage.emailSent', { name: label(row) });
    const body = row.meta?.body;
    const html = row.meta?.html;

    return (
        <Box
            component="li"
            aria-label={title}
            sx={{ display: 'grid', gridTemplateColumns: COLUMNS, columnGap: 1.5, pb: 1.75 }}
        >
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textAlign: 'right', pt: '2px', fontVariantNumeric: 'tabular-nums' }}
            >
                {clock(row.occurred_at)}
            </Typography>
            <Box aria-hidden="true" sx={{ display: 'flex', justifyContent: 'center', pt: '1px' }}>
                <MailOutline sx={{ fontSize: 16, color: 'text.secondary' }} />
            </Box>
            <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" component="h4" sx={{ m: 0, fontWeight: 500 }}>
                        {title}
                    </Typography>
                    {row.subject_type === 'campaign' && row.subject_id && (
                        <CampaignRef id={row.subject_id} name={row.subject_name} />
                    )}
                </Box>
                {row.meta?.subject && (
                    <Typography variant="caption" color="text.secondary">
                        {t('Modules.Admin.Usage.emailSubject', { subject: row.meta.subject })}
                    </Typography>
                )}
                {html || body ? (
                    <>
                        <ButtonBase
                            onClick={() => setOpen(!open)}
                            aria-expanded={open}
                            sx={{
                                alignSelf: 'flex-start',
                                typography: 'caption',
                                color: 'text.secondary',
                                borderRadius: 1,
                                px: 0.25,
                                '&:hover': { color: 'text.primary' },
                            }}
                        >
                            {open
                                ? t('Modules.Admin.Usage.hideEmail')
                                : t('Modules.Admin.Usage.showEmail')}
                        </ButtonBase>
                        <Collapse in={open} unmountOnExit>
                            <Box
                                sx={{
                                    mt: 0.5,
                                    px: 1.5,
                                    py: 1.25,
                                    borderRadius: 2,
                                    bgcolor: 'action.hover',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                }}
                            >
                                {row.meta?.to && row.meta.to.length > 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                        {t('Modules.Admin.Usage.emailTo', {
                                            to: row.meta.to.join(', '),
                                        })}
                                    </Typography>
                                )}
                                {html ? (
                                    // An empty `sandbox` runs no scripts, opens nothing and can't
                                    // reach this page: the HTML is someone's email, shown as-is.
                                    <Box
                                        component="iframe"
                                        title={title}
                                        sandbox=""
                                        srcDoc={html}
                                        sx={{
                                            width: '100%',
                                            height: 480,
                                            border: 0,
                                            borderRadius: 1,
                                            bgcolor: '#FFFFFF',
                                        }}
                                    />
                                ) : (
                                    <Typography
                                        variant="body2"
                                        component="p"
                                        sx={{
                                            m: 0,
                                            whiteSpace: 'pre-wrap',
                                            overflowWrap: 'anywhere',
                                        }}
                                    >
                                        {body}
                                    </Typography>
                                )}
                            </Box>
                        </Collapse>
                    </>
                ) : (
                    row.meta && (
                        <Typography variant="caption" color="text.secondary">
                            {t('Modules.Admin.Usage.emailNoBody')}
                        </Typography>
                    )
                )}
            </Box>
        </Box>
    );
};

const Session = ({ session }: { session: TimelineSession }) => {
    const t = useCommonTranslation();
    const clock = useClock();
    const deviceLabel = useDeviceLabel();
    const times = session.groups.map(groupTime).filter(Boolean).sort();
    const withDevice = session.groups
        .map(groupRow)
        .find(row => row?.browser != null || row?.user_agent != null);
    const span =
        times.length > 1
            ? `${clock(times[0])}–${clock(times[times.length - 1])}`
            : clock(times[0] ?? '');
    const device = withDevice ? deviceLabel({ ...withDevice, device_type: null }) : '';

    if (session.emails) {
        return (
            <Box component="ol" sx={{ listStyle: 'none', m: 0, mb: 0.75, p: 0 }}>
                {session.groups.map(group =>
                    group.email ? <EmailLine key={group.key} row={group.email} /> : null,
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 0.75 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: COLUMNS, columnGap: 1.5, mb: 0.5 }}>
                <span />
                <span />
                <Typography variant="caption" color="text.secondary">
                    <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {t('Modules.Admin.Usage.session')}
                    </Box>
                    {[span, device].filter(Boolean).map(part => ` · ${part}`)}
                </Typography>
            </Box>
            <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {session.groups.map(group => (
                    <Visit key={group.key} group={group} />
                ))}
            </Box>
        </Box>
    );
};

/** "Today · Thu 30 Oct", "Yesterday · Wed 29 Oct", or the date. */
const useDayLabel = () => {
    const t = useCommonTranslation();
    return (date: string) => {
        if (!date) return '';
        const day = parseISO(date);
        const named = format(day, 'EEE d MMM');
        if (isToday(day)) return `${t('Modules.Admin.Usage.today')} · ${named}`;
        if (isYesterday(day)) return `${t('Modules.Admin.Usage.yesterday')} · ${named}`;
        return format(day, 'EEE d MMM yyyy');
    };
};

const UsageTimeline = ({ rows }: { rows: UsageTimelineItem[] }) => {
    const t = useCommonTranslation();
    const dayLabel = useDayLabel();
    const formatDuration = useFormatDuration();
    const days = dayAndSession(mergeBackground(groupTimeline(rows)));

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
            {days.map(day => {
                const label = dayLabel(day.date);
                const time = day.sessions
                    .flatMap(session => session.groups)
                    .reduce((sum, group) => sum + (group.visit?.dwell_ms ?? 0), 0);
                return (
                    <Box key={day.key} component="section" aria-label={label}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
                            <Typography
                                component="h3"
                                variant="overline"
                                sx={{
                                    m: 0,
                                    lineHeight: 1.4,
                                    letterSpacing: '0.08em',
                                    fontWeight: 600,
                                }}
                            >
                                {label}
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                            <Typography variant="caption" color="text.secondary">
                                {t('Modules.Admin.Usage.daySummary', {
                                    count: day.sessions.filter(session => !session.emails).length,
                                    time: formatDuration(time),
                                })}
                            </Typography>
                        </Box>
                        {day.sessions.map(session => (
                            <Session key={session.key} session={session} />
                        ))}
                    </Box>
                );
            })}
        </Box>
    );
};

export default UsageTimeline;
